import { Base } from '../../abstract/base.model';
import { ApplicationTypeInfo } from './application-type-info.model';
import { AppliedChemical } from './applied-chemical.model';
import { DroneFlightEntry } from './drone-flight-entry.model';
import { EmergencyContactInfo } from './emergency-contact-info.model';
import { IncidentInformation } from './incident-information.model';
import { LoadMixLocation } from './load-mix-location.model';
import { SafetyComplianceInfo } from './safety-compliance-info.model';
import { SprayDetails } from './spray-details.model';
import { SprayRecordStatus } from './spray-record-status.model';
import { VisualObserverInfo } from './visual-observer-info.model';
import { WeatherConditions } from './weather-conditions.model';
import { isRecord } from '../utils/is-record';
import {
  toAppliedChemical,
  toApplicationTypeInfo,
  toDroneFlightEntry,
  toEmergencyContactInfo,
  toIncidentInformation,
  toLoadMixLocation,
  toSafetyComplianceInfo,
  toSprayDetails,
  toVisualObserverInfo,
  toWeatherConditions,
} from '../utils/spray-record-wire-utils';
import { listOf, nullableBoolean, nullableDecimal, nullableString } from '../utils/wire-value-utils';

/**
 * A spray record (mission) with its chemicals and drone flights, as `GET /spray-records/:id` returns it
 * (hub `SprayRecordService.getWithRelated`). The hub nests groups of the record's columns (weather, spray
 * details, incidents, …); those groups are always present here, and a value inside one that the hub didn't
 * send stays `undefined`. The list endpoint returns `SprayRecordSummary` rows instead.
 *
 * The record is never sent back: the form builds a `SprayRecordRequest` (see `spray-record-request-utils.ts`).
 */
export class SprayRecord extends Base {
  organizationId?: string | null;
  organizationName?: string | null;
  workOrderId?: string | null;

  // Application details
  applicationDate?: string | null;
  status?: SprayRecordStatus | null;

  // Field (single per record) and its customer
  fieldId?: string | null;
  fieldName?: string | null;
  fieldAcreage?: number | null;
  customerId?: string | null;
  customerName?: string | null;

  sprayDetails: SprayDetails;

  // Chemicals (shared across all drones - same mix required)
  chemicals: AppliedChemical[];
  totalVolume?: string | null;
  tankMixNotes?: string | null;

  // Operating certification (snapshot from when the record was created)
  certificationId?: string | null;
  certificationName?: string | null;
  certificationNumber?: string | null;
  certificationState?: string | null;
  certificationExpiration?: string | null;

  // Organization FAA certifications snapshot
  orgFaa44807Number?: string | null;
  orgFaa44807Expiration?: string | null;
  orgFaaPart137Number?: string | null;
  orgFaaPart137Expiration?: string | null;

  visualObserverInfo: VisualObserverInfo;
  droneFlights: DroneFlightEntry[];
  weather: WeatherConditions;
  incidentInfo: IncidentInformation;
  safetyCompliance: SafetyComplianceInfo;
  emergencyContact: EmergencyContactInfo;
  applicationTypeInfo: ApplicationTypeInfo;
  loadMixLocation: LoadMixLocation;

  // Compliance checklist
  preFlightChecklist?: boolean | null;
  bufferZonesVerified?: boolean | null;

  notes?: string | null;
  createdBy?: string | null;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    const field = isRecord(source['field']) ? source['field'] : {};
    const customer = isRecord(source['customer']) ? source['customer'] : {};
    this.organizationId = nullableString(source['organizationId']);
    this.organizationName = nullableString(source['organizationName']);
    this.workOrderId = nullableString(source['workOrderId']);
    this.applicationDate = nullableString(source['applicationDate']);
    this.status = nullableString(source['status']) as SprayRecordStatus | null | undefined;
    this.fieldId = nullableString(source['fieldId']);
    this.fieldName = nullableString(field['name']);
    this.fieldAcreage = nullableDecimal(field['acreage']);
    this.customerId = nullableString(customer['id']);
    this.customerName = nullableString(customer['name']);
    this.sprayDetails = toSprayDetails(source['sprayDetails']);
    this.chemicals = listOf(source['chemicals'], toAppliedChemical);
    this.totalVolume = nullableString(source['totalVolume']);
    this.tankMixNotes = nullableString(source['tankMixNotes']);
    this.certificationId = nullableString(source['certificationId']);
    this.certificationName = nullableString(source['certificationName']);
    this.certificationNumber = nullableString(source['certificationNumber']);
    this.certificationState = nullableString(source['certificationState']);
    this.certificationExpiration = nullableString(source['certificationExpiration']);
    this.orgFaa44807Number = nullableString(source['orgFaa44807Number']);
    this.orgFaa44807Expiration = nullableString(source['orgFaa44807Expiration']);
    this.orgFaaPart137Number = nullableString(source['orgFaaPart137Number']);
    this.orgFaaPart137Expiration = nullableString(source['orgFaaPart137Expiration']);
    this.visualObserverInfo = toVisualObserverInfo(source['visualObserverInfo']);
    this.droneFlights = listOf(source['droneFlights'], toDroneFlightEntry);
    this.weather = toWeatherConditions(source['weather']);
    this.incidentInfo = toIncidentInformation(source['incidentInfo']);
    this.safetyCompliance = toSafetyComplianceInfo(source['safetyCompliance']);
    this.emergencyContact = toEmergencyContactInfo(source['emergencyContact']);
    this.applicationTypeInfo = toApplicationTypeInfo(source['applicationTypeInfo']);
    this.loadMixLocation = toLoadMixLocation(source['loadMixLocation']);
    this.preFlightChecklist = nullableBoolean(source['preFlightChecklist']);
    this.bufferZonesVerified = nullableBoolean(source['bufferZonesVerified']);
    this.notes = nullableString(source['notes']);
    this.createdBy = nullableString(source['createdBy']);
  }
}

